/*
Capture repository — write path for user discoveries, mirroring the Android
CaptureRepository contract (insert, exists, list).
*/

import { kStores, withTx } from './db'
import type { Capture } from './types'

/*
Insert a capture. Throws if (arbreId, timestamp) already exists — the unique
index on the composite key gives us idempotent re-imports for free.
*/
export async function insertCapture(inCapture: Capture): Promise<number>
{
	return withTx([kStores.captures], 'readwrite', (inTx) => {
		return new Promise<number>((inResolve, inReject) => {
			const vReq = inTx.objectStore(kStores.captures).add(inCapture)  // add returns the autoincremented id
			vReq.onsuccess = () => inResolve(vReq.result as number)
			vReq.onerror = () => inReject(vReq.error)
		})
	})
}

/*
Check whether a (arbreId, timestamp) pair already exists in the captures store.
Used by the backup importer to dedup re-imports.
*/
export async function captureExists(
	inArbreId: number,
	inTimestamp: number,
): Promise<boolean>
{
	return withTx([kStores.captures], 'readonly', (inTx) => {
		return new Promise<boolean>((inResolve, inReject) => {
			const vIndex = inTx.objectStore(kStores.captures).index(kStores.indexCapturesByArbre)
			const vReq = vIndex.getKey([inArbreId, inTimestamp])
			vReq.onsuccess = () => inResolve(vReq.result !== undefined)
			vReq.onerror = () => inReject(vReq.error)
		})
	})
}

/*
Return all captures, oldest first. Cheap enough at expected volumes (personal
use, low thousands of rows).
*/
export async function listCaptures(): Promise<Capture[]>
{
	return withTx([kStores.captures], 'readonly', (inTx) => {
		return new Promise<Capture[]>((inResolve, inReject) => {
			const vReq = inTx.objectStore(kStores.captures).getAll()
			vReq.onsuccess = () => inResolve((vReq.result as Capture[]).sort((inA, inB) => inA.timestamp - inB.timestamp))
			vReq.onerror = () => inReject(vReq.error)
		})
	})
}

/*
Set of arbreIds the user has captured at least once. Used by the map layer
to colour discovered points green and clusters by ratio.
*/
export async function capturedArbreIds(): Promise<Set<number>>
{
	const vList = await listCaptures()  // all captures, then project ids
	return new Set(vList.map((inCap) => inCap.arbreId))
}

/*
Set of speciesIndex values the user has captured at least once. Drives
the "Inconnu / révélé" gating in the arbre detail sheet — a tree's
species name is hidden until any individual of that species has been
captured (post-capture-reveal loop).
*/
export async function capturedSpeciesIndices(): Promise<Set<number>>
{
	const vList = await listCaptures()
	return new Set(vList.map((inCap) => inCap.speciesIndex))
}

export interface SpeciesCaptureGroup
{
	speciesIndex: number
	captureCount: number
	arbreIds: Set<number>
	firstCaptureAt: number  // earliest timestamp across the group
	lastCaptureAt: number  // most recent timestamp, useful for sort-by-recent
	photoFilenames: string[]  // ordered most-recent first, for the Arboretum gallery strip
}

/*
Group all captures by speciesIndex and return an array sorted by oldest first
capture (so the Arboretum lists species in discovery order). Each group
exposes the count of captures, the unique arbreIds touched, and the earliest
+ latest timestamps.
*/
export async function groupCapturesBySpecies(inSeasonOrdinal: number | null = null): Promise<SpeciesCaptureGroup[]>
{
	const vList = await listCaptures()  // already sorted oldest-first
	const vGroups = new Map<number, SpeciesCaptureGroup>()  // speciesIndex -> mutable accumulator
	const vCapturesPerSpecies = new Map<number, Capture[]>()  // capture rows per species, used to derive ordered photoFilenames
	for (const vCap of vList)
	{
		if (inSeasonOrdinal !== null && vCap.season !== inSeasonOrdinal) continue
		const vExisting = vGroups.get(vCap.speciesIndex)  // current accumulator for this species, if any
		if (vExisting)
		{
			vExisting.captureCount += 1
			vExisting.arbreIds.add(vCap.arbreId)
			if (vCap.timestamp < vExisting.firstCaptureAt) vExisting.firstCaptureAt = vCap.timestamp
			if (vCap.timestamp > vExisting.lastCaptureAt) vExisting.lastCaptureAt = vCap.timestamp
		}
		else
		{
			vGroups.set(vCap.speciesIndex, {
				speciesIndex: vCap.speciesIndex,
				captureCount: 1,
				arbreIds: new Set([vCap.arbreId]),
				firstCaptureAt: vCap.timestamp,
				lastCaptureAt: vCap.timestamp,
				photoFilenames: [],
			})
		}
		const vBucket = vCapturesPerSpecies.get(vCap.speciesIndex) ?? []  // captures buffer for this species
		vBucket.push(vCap)
		vCapturesPerSpecies.set(vCap.speciesIndex, vBucket)
	}

	for (const [vSk, vGroup] of vGroups)
	{
		const vCaptures = vCapturesPerSpecies.get(vSk) ?? []
		vGroup.photoFilenames = vCaptures
			.slice()
			.sort((inA, inB) => inB.timestamp - inA.timestamp)
			.map((inC) => inC.photoFilename)
			.filter((inN): inN is string => typeof inN === 'string' && inN.length > 0)
	}

	return [...vGroups.values()].sort((inA, inB) => inA.firstCaptureAt - inB.firstCaptureAt)
}
